import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  message,
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tabs,
  Tag
} from 'antd'
import moment from 'moment'
import { getMaterial } from '../../modules/material'
import {
  ALIAS_STATUS,
  getAliasCandidates,
  getSearchMiss,
  saveMaterialAlias
} from '../../modules/materialAlias'

const PAGE_SIZE = 20
const DATE_FORMAT = 'YYYY-MM-DD'

const SOURCE_LABEL = {
  SHOP_NAME: 'ชื่อสินค้าในร้าน',
  SEARCH_PICK: 'เลือกจากผลการค้น',
  ADMIN: 'แอดมิน'
}

const materialLabel = (material) =>
  material.commonName
    ? `${material.name} (${material.commonName})`
    : `${material.name}`

const defaultRange = () => [moment().subtract(30, 'days'), moment()]

// คำเรียกอื่นของวัสดุมาตรฐาน: คำที่ร้านใช้จริงซึ่งรอแอดมินตัดสิน และคำที่ร้านค้นแล้วไม่เจอ (spec 017 A1–A5)
const MaterialAlias = () => {
  const dispatch = useDispatch()
  const [page, setPage] = useState(0)
  const [range, setRange] = useState(defaultRange())
  const [decidingKey, setDecidingKey] = useState(null)
  const [missRow, setMissRow] = useState(null)
  const [materialId, setMaterialId] = useState(undefined)
  const [modalError, setModalError] = useState('')
  const [isSaving, setSaving] = useState(false)
  const [assigned, setAssigned] = useState({})

  const {
    candidates,
    isCandidateLoading,
    searchMiss,
    isMissLoading,
    materials
  } = useSelector(
    (state) => ({
      candidates: state.materialAlias.candidates,
      isCandidateLoading: state.materialAlias.isCandidateLoading,
      searchMiss: state.materialAlias.searchMiss,
      isMissLoading: state.materialAlias.isMissLoading,
      materials: state.material.material.data
    }),
    []
  )

  const loadCandidates = (nextPage) => {
    setPage(nextPage)
    return dispatch(getAliasCandidates(nextPage, PAGE_SIZE))
  }

  const loadSearchMiss = (nextRange) =>
    dispatch(
      getSearchMiss(
        nextRange[0].format(DATE_FORMAT),
        nextRange[1].format(DATE_FORMAT)
      )
    )

  useEffect(() => {
    loadCandidates(0)
    loadSearchMiss(range)
    dispatch(getMaterial())
    // โหลดครั้งแรกเมื่อเปิดหน้า
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const candidateKey = (row) => `${row.materialId}|${row.alias}`

  const decide = async (row, status) => {
    setDecidingKey(candidateKey(row))
    const result = await dispatch(
      saveMaterialAlias({
        materialId: row.materialId,
        alias: row.alias,
        status,
        source: row.source
      })
    )
    setDecidingKey(null)
    if (!result.success) {
      message.error(result.message)
      return
    }
    message.success(
      status === ALIAS_STATUS.APPROVED
        ? `อนุมัติ "${row.alias}" แล้ว`
        : `ปฏิเสธ "${row.alias}" แล้ว`
    )
    // แถวสุดท้ายของหน้าที่ไม่ใช่หน้าแรกหายไป ให้ถอยกลับหนึ่งหน้า
    const isLastRowOfPage = (candidates.content || []).length === 1
    loadCandidates(isLastRowOfPage && page > 0 ? page - 1 : page)
  }

  const onChangeRange = (nextRange) => {
    const value =
      nextRange && nextRange.length === 2 ? nextRange : defaultRange()
    setRange(value)
    loadSearchMiss(value)
  }

  const openAssign = (row) => {
    setMissRow(row)
    setMaterialId(undefined)
    setModalError('')
  }

  const closeAssign = () => {
    setMissRow(null)
    setModalError('')
  }

  const onAssign = async () => {
    if (!materialId) {
      setModalError('กรุณาเลือกวัสดุมาตรฐาน')
      return
    }
    setSaving(true)
    setModalError('')
    const result = await dispatch(
      saveMaterialAlias({
        materialId,
        alias: missRow.keyword,
        status: ALIAS_STATUS.APPROVED
      })
    )
    setSaving(false)
    if (!result.success) {
      // backend ปฏิเสธ (เช่น มีคำนี้อยู่แล้ว) — แสดงเหตุผลและไม่ปิด modal
      setModalError(result.message)
      return
    }
    const material = (materials || []).find((item) => item.id === materialId)
    setAssigned((prev) => ({
      ...prev,
      [missRow.keyword]: material ? material.name : String(materialId)
    }))
    message.success(`ตั้ง "${missRow.keyword}" เป็นคำเรียกอื่นแล้ว`)
    closeAssign()
  }

  const candidateColumns = [
    {
      title: 'วัสดุมาตรฐาน',
      dataIndex: 'materialName',
      key: 'materialName'
    },
    {
      title: 'คำที่ร้านเรียก',
      dataIndex: 'alias',
      key: 'alias'
    },
    {
      title: 'จำนวนร้าน',
      dataIndex: 'shopCount',
      key: 'shopCount',
      width: 120,
      align: 'center'
    },
    {
      title: 'แหล่งที่มา',
      dataIndex: 'source',
      key: 'source',
      width: 180,
      render: (source) => SOURCE_LABEL[source] || source
    },
    {
      title: 'Action',
      key: 'action',
      width: 200,
      render: (text, row) => (
        <div>
          <Button
            type="primary"
            size="small"
            loading={decidingKey === candidateKey(row)}
            onClick={() => decide(row, ALIAS_STATUS.APPROVED)}>
            อนุมัติ
          </Button>
          <Divider type="vertical" />
          <Popconfirm
            title={`ปฏิเสธ "${row.alias}" ? คำนี้จะไม่กลับมาในรายการรออนุมัติ`}
            okText="ปฏิเสธ"
            cancelText="ยกเลิก"
            onConfirm={() => decide(row, ALIAS_STATUS.REJECTED)}>
            <Button type="danger" size="small">
              ปฏิเสธ
            </Button>
          </Popconfirm>
        </div>
      )
    }
  ]

  const missColumns = [
    {
      title: 'คำค้น',
      dataIndex: 'keyword',
      key: 'keyword'
    },
    {
      title: 'จำนวนร้าน',
      dataIndex: 'shopCount',
      key: 'shopCount',
      width: 120,
      align: 'center'
    },
    {
      title: 'ค้นล่าสุด',
      dataIndex: 'lastSearchedAt',
      key: 'lastSearchedAt',
      width: 180,
      render: (value) =>
        value ? moment(value).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: 'Action',
      key: 'action',
      width: 320,
      render: (text, row) => (
        <div>
          <Button size="small" onClick={() => openAssign(row)}>
            ตั้งเป็นคำเรียกอื่นของ…
          </Button>
          {assigned[row.keyword] && (
            <Tag color="green" style={{ marginLeft: 8 }}>
              {assigned[row.keyword]}
            </Tag>
          )}
        </div>
      )
    }
  ]

  return (
    <Card title="Material Alias">
      <Tabs defaultActiveKey="candidates">
        <Tabs.TabPane tab="รออนุมัติ" key="candidates">
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="คำที่ร้านใช้เรียกวัสดุมาตรฐานจริง เรียงตามจำนวนร้าน คำที่อนุมัติแล้วร้านอื่นจะค้นเจอภายใน 5 นาที"
          />
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button
              size="small"
              icon="reload"
              onClick={() => loadCandidates(page)}>
              Reload
            </Button>
          </div>
          <Table
            loading={isCandidateLoading}
            rowKey={candidateKey}
            dataSource={candidates.content}
            columns={candidateColumns}
            pagination={{
              current: page + 1,
              pageSize: PAGE_SIZE,
              total: candidates.total,
              onChange: (nextPage) => loadCandidates(nextPage - 1)
            }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="คำที่ค้นไม่เจอ" key="searchMiss">
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8 }}>ช่วงวันที่</span>
            <DatePicker.RangePicker
              value={range}
              allowClear={false}
              format={DATE_FORMAT}
              onChange={onChangeRange}
            />
            <Button
              style={{ marginLeft: 8 }}
              icon="reload"
              onClick={() => loadSearchMiss(range)}>
              Reload
            </Button>
          </div>
          <Table
            loading={isMissLoading}
            rowKey={(row) => row.keyword}
            dataSource={searchMiss}
            columns={missColumns}
            pagination={{ pageSize: PAGE_SIZE }}
          />
        </Tabs.TabPane>
      </Tabs>
      <Modal
        title="ตั้งเป็นคำเรียกอื่น"
        visible={!!missRow}
        okText="บันทึก"
        cancelText="ยกเลิก"
        confirmLoading={isSaving}
        onOk={onAssign}
        onCancel={closeAssign}>
        <p>
          คำค้น: <b>{missRow ? missRow.keyword : ''}</b>
        </p>
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="เลือกวัสดุมาตรฐาน (พิมพ์ชื่อเพื่อค้น)"
          value={materialId}
          onChange={setMaterialId}
          filterOption={(input, option) =>
            String(option.props.children)
              .toLowerCase()
              .includes(input.toLowerCase())
          }>
          {(materials || []).map((material) => (
            <Select.Option key={material.id} value={material.id}>
              {materialLabel(material)}
            </Select.Option>
          ))}
        </Select>
        {modalError && (
          <Alert
            style={{ marginTop: 16 }}
            type="error"
            showIcon
            message={modalError}
          />
        )}
      </Modal>
    </Card>
  )
}

export default MaterialAlias
